/**
 * Consolidated single-prescription report (GP-1177).
 *
 * The patient chart scatters one Rx's verification, counseling checklist, and
 * notes across separate sub-tabs (RX History / Counseling History / All Notes)
 * with no way to print or share a single document per prescription. This module
 * composes the already-loaded per-Rx data (the same objects the RX History tab
 * renders) into one printable/shareable report, opened in a new window that
 * auto-prints (Save as PDF from the print dialog to share).
 *
 * Pure HTML string builder (no React/MUI) so it can be unit-tested and reuses
 * the shared ISEC checklist + counseling-status sources of truth rather than
 * duplicating labels (see CLAUDE.md: one source of truth for shared data).
 */
import { format } from 'date-fns';
import {
  ISEC_QUESTIONS,
  ISEC_POINT_IDS,
} from '../../constants/isecQuestions';
import { toIsecPoints } from '../counseling/counselingChecklist';
import {
  getCounselingStatusDisplay,
  getDeclinedReasonLabel,
} from '../../constants/counselingConstants';
import { rxService } from '../../services/rxService';
import { ImageConverter } from './rx/utils/imageConverter';

const METHOD_LABELS: Record<string, string> = {
  by_phone: 'By Phone',
  in_person: 'In Person',
  video: 'Video',
  written: 'Written',
};

const BY_LABELS: Record<string, string> = {
  patient: 'Patient',
  caregiver: 'Caregiver',
  representative: 'Representative',
};

const IESC_LABELS: Record<string, string> = {
  indication: 'Indication',
  effectiveness: 'Effectiveness',
  safety: 'Safety',
  compliance: 'Compliance',
};

/** Minimal shapes the report consumes — a structural subset of RxTimelineTab's
 *  own interfaces, so the tab can pass its entries straight through. */
export interface RxReportCounselingRecord {
  status: string;
  method?: string;
  counselingBy?: string;
  declinedReason?: string;
  comments?: string;
  doctorName?: string;
  completedBy?: string;
  createdBy?: string;
  createdAt?: string;
  completedAt?: string;
  checklist?: Record<string, { checked: boolean; notes: string }>;
}

export interface RxReportNote {
  content: string;
  type?: string;
  author?: { name?: string };
  createdAt?: string;
}

export interface RxReportEntry {
  rxNumber: string;
  medicationName: string;
  orderNumber?: string;
  verifiedAt?: string;
  verifiedBy?: string;
  daysSupply?: number;
  verifiedDaysSupply?: number;
  drugVerification?: any;
  counselingRecords: RxReportCounselingRecord[];
  notes: RxReportNote[];
}

/** A rendered hardcopy/script image ready to embed inline in the report. */
export interface RxReportImage {
  label: string;
  /** data: URL (embeds inline so it survives the popup window / printing). */
  dataUrl: string;
}

export interface RxReportOptions {
  patientName?: string;
  /** Resolve an auth0/user id to a display name (from RxTimelineTab's batch lookup). */
  resolveUser?: (id: string | undefined | null) => string;
  /** Hardcopy / script images to embed (fetched via {@link fetchReportHardcopyImages}). */
  hardcopyImages?: RxReportImage[];
  /** Count of hardcopy attachments that couldn't be embedded inline (e.g. PDFs)
   *  so the report can still tell the pharmacist to open View Hardcopy for them. */
  hardcopyUnavailable?: number;
}

function escapeHtml(str: string | undefined | null): string {
  if (str === undefined || str === null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return escapeHtml(dateStr);
  return format(d, 'MMM d, yyyy');
}

function fmtDateTime(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return escapeHtml(dateStr);
  return format(d, 'MMM d, yyyy h:mm a');
}

function verificationSection(entry: RxReportEntry, resolveUser: (v: string | undefined | null) => string): string {
  const dv = entry.drugVerification;
  const iescRow = Object.entries(IESC_LABELS)
    .map(([key, label]) => {
      const done = !!dv?.clinicalChecks?.[key];
      return `<span class="chip ${done ? 'chip-on' : 'chip-off'}">${label.charAt(0)}${done ? ' ✓' : ''}</span>`;
    })
    .join(' ');

  const clinicalNotes =
    dv?.clinicalNotes && typeof dv.clinicalNotes === 'object'
      ? Object.entries(dv.clinicalNotes)
          .filter(([, v]) => v)
          .map(
            ([key, v]) =>
              `<div><strong>${escapeHtml(IESC_LABELS[key] || key)}:</strong> ${escapeHtml(v as string)}</div>`,
          )
          .join('')
      : '';

  const daysSupply = dv?.verifiedDaysSupply || entry.verifiedDaysSupply || entry.daysSupply || '—';
  const technical =
    dv?.technicalChecks && Object.values(dv.technicalChecks).every(Boolean) ? 'Verified' : '—';

  return `
  <div class="section-heading">Verification</div>
  <table>
    <tr>
      <td class="label-cell">Verified By</td>
      <td class="value-cell">${escapeHtml(resolveUser(entry.verifiedBy)) || '—'}</td>
      <td class="label-cell">Verified On</td>
      <td class="value-cell">${fmtDateTime(entry.verifiedAt)}</td>
    </tr>
    <tr>
      <td class="label-cell">Days Supply</td>
      <td class="value-cell">${escapeHtml(String(daysSupply))}</td>
      <td class="label-cell">Technical Check</td>
      <td class="value-cell">${technical}</td>
    </tr>
    <tr>
      <td class="label-cell">IESC</td>
      <td class="value-cell" colspan="3">${iescRow}</td>
    </tr>
    ${clinicalNotes ? `<tr><td class="label-cell">Clinical Notes</td><td class="value-cell" colspan="3">${clinicalNotes}</td></tr>` : ''}
  </table>`;
}

function counselingRecordHtml(rec: RxReportCounselingRecord, resolveUser: (v: string | undefined | null) => string): string {
  const status = getCounselingStatusDisplay(rec.status, rec.declinedReason);
  const points = toIsecPoints(rec.checklist);
  const checkedCount = ISEC_POINT_IDS.filter((id) => points[id]?.checked).length;

  const checklistGroups = ISEC_QUESTIONS.map((group) => {
    const items = group.questions
      .map((q) => {
        const value = points[q.id] || { checked: false, notes: '' };
        const mark = value.checked ? '☑' : '☐';
        const notes = value.notes
          ? `<div class="point-notes">${escapeHtml(value.notes)}</div>`
          : '';
        return `<div class="point ${value.checked ? 'point-done' : ''}"><span class="mark">${mark}</span> ${escapeHtml(q.text)}${notes}</div>`;
      })
      .join('');
    return `<div class="checklist-group"><div class="group-title">${escapeHtml(group.title)}</div>${items}</div>`;
  }).join('');

  return `
  <div class="counseling-card">
    <div class="counseling-header">
      <span class="status-badge status-${status.color}">${escapeHtml(status.label)}</span>
      <span class="meta">${escapeHtml(METHOD_LABELS[rec.method || ''] || rec.method || '—')}</span>
      <span class="meta">Counseled to: ${escapeHtml(BY_LABELS[rec.counselingBy || ''] || rec.counselingBy || '—')}</span>
      <span class="meta">By: ${escapeHtml(resolveUser(rec.completedBy || rec.createdBy)) || '—'}</span>
      <span class="meta">${fmtDateTime(rec.completedAt || rec.createdAt)}</span>
    </div>
    ${rec.declinedReason ? `<div class="declined">Declined reason: ${escapeHtml(getDeclinedReasonLabel(rec.declinedReason))}</div>` : ''}
    <div class="checklist-title">Counseling Checklist (${checkedCount}/${ISEC_POINT_IDS.length} items covered)</div>
    <div class="checklist">${checklistGroups}</div>
    ${rec.comments ? `<div class="comments"><strong>Comments:</strong> ${escapeHtml(rec.comments)}</div>` : ''}
  </div>`;
}

function counselingSection(entry: RxReportEntry, resolveUser: (v: string | undefined | null) => string): string {
  const body = entry.counselingRecords.length
    ? entry.counselingRecords.map((rec) => counselingRecordHtml(rec, resolveUser)).join('')
    : '<div class="empty">No counseling record for this prescription.</div>';
  return `<div class="section-heading">Counseling</div>${body}`;
}

function notesSection(entry: RxReportEntry): string {
  const body = entry.notes.length
    ? entry.notes
        .map(
          (note) => `
      <div class="note ${note.type === 'clinical_verification' ? 'note-cv' : ''}">
        ${note.type === 'clinical_verification' ? '<span class="note-tag">Clinical Verification</span>' : ''}
        <div class="note-content">${escapeHtml(note.content)}</div>
        <div class="note-meta">${escapeHtml(note.author?.name) || 'Unknown'} · ${fmtDateTime(note.createdAt)}</div>
      </div>`,
        )
        .join('')
    : '<div class="empty">No notes for this prescription.</div>';
  return `<div class="section-heading">Notes (${entry.notes.length})</div>${body}`;
}

function hardcopySection(opts: RxReportOptions): string {
  const images = opts.hardcopyImages || [];
  const unavailable = opts.hardcopyUnavailable || 0;
  if (!images.length && !unavailable) return '';

  const imgs = images
    .map(
      (img) => `
      <div class="hardcopy-item">
        <div class="hardcopy-label">${escapeHtml(img.label)}</div>
        <img class="hardcopy-img" src="${img.dataUrl}" alt="${escapeHtml(img.label)}" />
      </div>`,
    )
    .join('');

  const note = unavailable
    ? `<div class="empty">${unavailable} attachment(s) could not be embedded here (e.g. multi-page PDFs) — open "View Hardcopy" to print those.</div>`
    : '';

  return `<div class="section-heading">Hardcopy / Script Images</div>${imgs}${note}`;
}

/**
 * Build the full printable HTML document for a single prescription's
 * consolidated report.
 */
export function buildRxReportHtml(entry: RxReportEntry, opts: RxReportOptions = {}): string {
  const resolveUser = opts.resolveUser || ((v) => v || '');
  const now = format(new Date(), 'MMM d, yyyy h:mm a');

  return `<!DOCTYPE html>
<html>
<head>
  <title>Prescription Report - RX #${escapeHtml(entry.rxNumber)}</title>
  <style>
    @page { size: letter; margin: 0.75in; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #333; padding: 20px; }
    .report-header { margin-bottom: 20px; border-bottom: 4px solid #1E4D8C; padding-bottom: 12px; }
    .report-title { font-size: 24px; font-weight: bold; color: #1E4D8C; margin-bottom: 4px; }
    .report-subtitle { font-size: 16px; color: #555; margin-bottom: 6px; }
    .report-meta { font-size: 11px; color: #777; }
    .section-heading { font-size: 15px; font-weight: bold; color: #1E4D8C; border-bottom: 2px solid #1E4D8C; padding-bottom: 4px; margin: 24px 0 10px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
    td { border: 1px solid #DDD; padding: 7px 10px; font-size: 12px; vertical-align: top; }
    .label-cell { background: #EEF3FA; font-weight: bold; color: #1E4D8C; width: 22%; }
    .value-cell { width: 28%; }
    .chip { display: inline-block; padding: 2px 7px; border: 1px solid #999; border-radius: 10px; font-size: 11px; font-weight: bold; }
    .chip-on { background: #E6F4EA; border-color: #2E7D32; color: #2E7D32; }
    .chip-off { color: #999; }
    .counseling-card { border: 1px solid #DDD; border-radius: 4px; padding: 12px; margin-bottom: 12px; }
    .counseling-header { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; margin-bottom: 8px; }
    .status-badge { padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: bold; color: #fff; }
    .status-success { background: #2E7D32; }
    .status-error { background: #C62828; }
    .status-warning { background: #ED6C02; }
    .status-default { background: #757575; }
    .meta { font-size: 11px; color: #555; }
    .declined { color: #ED6C02; font-size: 11px; margin-bottom: 6px; }
    .checklist-title { font-weight: bold; color: #1E4D8C; margin: 6px 0; font-size: 12px; }
    .checklist { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 20px; }
    .checklist-group { break-inside: avoid; }
    .group-title { font-weight: bold; font-size: 11px; color: #333; margin-bottom: 3px; border-bottom: 1px solid #EEE; }
    .point { font-size: 11px; color: #777; padding: 1px 0; }
    .point-done { color: #222; }
    .mark { font-family: monospace; }
    .point-notes { margin-left: 16px; font-style: italic; color: #666; font-size: 10px; }
    .comments { margin-top: 8px; font-size: 12px; }
    .note { border-left: 3px solid #DDD; padding: 4px 10px; margin-bottom: 8px; }
    .note-cv { border-left-color: #2E7D32; }
    .note-tag { display: inline-block; font-size: 10px; color: #2E7D32; border: 1px solid #2E7D32; border-radius: 8px; padding: 0 6px; margin-bottom: 3px; }
    .note-content { font-size: 12px; line-height: 1.4; }
    .note-meta { font-size: 10px; color: #888; }
    .empty { color: #999; font-style: italic; font-size: 12px; margin-bottom: 12px; }
    .hardcopy-item { margin-bottom: 14px; break-inside: avoid; }
    .hardcopy-label { font-size: 11px; font-weight: bold; color: #1E4D8C; margin-bottom: 4px; }
    .hardcopy-img { max-width: 100%; height: auto; border: 1px solid #CCC; }
    .footer { text-align: center; color: #AAA; font-size: 10px; margin-top: 30px; border-top: 2px solid #CCC; padding-top: 8px; }
    @media print { body { padding: 0; margin: 0; } -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  </style>
</head>
<body>
  <div class="report-header">
    <div class="report-title">PHOX Prescription Report</div>
    <div class="report-subtitle">RX #${escapeHtml(entry.rxNumber)} &middot; ${escapeHtml(entry.medicationName)}</div>
    <div class="report-meta">
      Patient: ${escapeHtml(opts.patientName) || 'N/A'}
      ${entry.orderNumber ? `&middot; Order: ${escapeHtml(entry.orderNumber)}` : ''}
      &middot; Report Date: ${now}
    </div>
  </div>

  ${verificationSection(entry, resolveUser)}
  ${counselingSection(entry, resolveUser)}
  ${notesSection(entry)}
  ${hardcopySection(opts)}

  <div class="footer">
    PHOX Platform &middot; PharmaGuide Inc. &middot; Confidential<br/>
    Generated: ${escapeHtml(new Date().toLocaleString('en-US'))}
  </div>
</body>
</html>`;
}

/**
 * Open a blank report window SYNCHRONOUSLY (call this directly inside the click
 * handler, before any `await`). Opening after an await loses the user-activation
 * gesture and browsers block the popup — so callers that must fetch data first
 * open the window here, then render into it once the data resolves.
 * Returns null when the popup is blocked.
 */
export function openReportWindow(): Window | null {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (win) {
    // Placeholder so the window isn't a jarring blank while data/images load.
    win.document.write(
      '<!DOCTYPE html><html><head><title>Generating report…</title></head>' +
        '<body style="font-family:Arial,sans-serif;color:#666;padding:40px">Generating report…</body></html>',
    );
  }
  return win;
}

/**
 * Trigger printing on a report window robustly. `window.print()` is driven from
 * the OPENER (not an inline `<body>` script) with an idempotent guard plus a
 * timeout fallback, so a missed `onload` — which can happen for a
 * `document.write`-populated window — doesn't leave the user staring at the
 * report with no print dialog (Sentry: silent print failure).
 */
function triggerPrint(win: Window): void {
  let printed = false;
  const doPrint = () => {
    if (printed || win.closed) return;
    printed = true;
    try {
      win.focus();
      win.print();
    } catch {
      /* user can still print manually from the report window */
    }
  };
  win.onload = doPrint;
  // Fallback: onload may already have fired (or never fire) for a written doc.
  win.setTimeout(doPrint, 600);
}

/**
 * Render the consolidated report into an already-open window and print it.
 */
export function renderRxReport(win: Window, entry: RxReportEntry, opts: RxReportOptions = {}): void {
  win.document.open();
  win.document.write(buildRxReportHtml(entry, opts));
  win.document.close();
  triggerPrint(win);
}

/**
 * Open the consolidated single-Rx report in a new window and trigger print.
 * Convenience wrapper for callers whose data is already in memory (no fetch).
 * The pharmacist can print or "Save as PDF" from the browser dialog to share.
 */
export function printRxReport(entry: RxReportEntry, opts: RxReportOptions = {}): void {
  const win = openReportWindow();
  if (!win) {
    // Pop-ups blocked — surface via caller's snackbar instead of a raw alert.
    throw new Error('POPUP_BLOCKED');
  }
  renderRxReport(win, entry, opts);
}

/**
 * Fetch a prescription's hardcopy / script-image attachments and render them to
 * inline data URLs for embedding in the report (GP-1177: the report must
 * consolidate the hardcopy, not just verification/counseling/notes). Best-effort
 * — image/attachment failures are swallowed so they never block the rest of the
 * report. Multi-page TIFFs contribute their first page; formats that can't be an
 * `<img>` (e.g. PDFs) are counted in `unavailable` instead of embedded.
 */
export async function fetchReportHardcopyImages(
  pharmacyId: string | undefined,
  patientId: string,
  rxNumber: string,
): Promise<{ images: RxReportImage[]; unavailable: number }> {
  if (!pharmacyId || !rxNumber || rxNumber === '—') {
    return { images: [], unavailable: 0 };
  }

  let attachments: Array<{
    scriptImageContentId?: string;
    scriptImageType?: number;
  }> = [];
  try {
    attachments = await rxService.getAllRxAttachments(pharmacyId, patientId, rxNumber);
  } catch {
    return { images: [], unavailable: 0 };
  }

  const images: RxReportImage[] = [];
  let unavailable = 0;
  let hardCopyCount = 0;

  for (const att of attachments) {
    if (!att.scriptImageContentId) continue;
    const label =
      att.scriptImageType === 1
        ? 'Script Image'
        : att.scriptImageType === 2
          ? `Hard Copy${++hardCopyCount > 1 ? ` ${hardCopyCount}` : ''}`
          : 'Document';
    try {
      const image = await rxService.getRxImage(pharmacyId, patientId, att.scriptImageContentId);
      const mime = (image.mimeType || '').toLowerCase();
      // PDFs can't be embedded as <img>; leave them to "View Hardcopy".
      if (mime.includes('pdf')) {
        unavailable++;
        continue;
      }
      const prepared = await ImageConverter.prepareImageForDisplay(
        image.content,
        image.mimeType,
        image.compressionType,
      );
      // Prefer an inline data URL so the image survives in the popup / print job.
      const dataUrl =
        prepared.tiffUrls && prepared.tiffUrls.length > 0
          ? prepared.tiffUrls[0]
          : prepared.dataUrl;
      if (dataUrl) {
        images.push({ label, dataUrl });
      } else {
        unavailable++;
      }
    } catch {
      unavailable++;
    }
  }

  return { images, unavailable };
}
